import React, { useState } from 'react';
import { OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { OpModal } from 'components/customAntd/DLS/OpModal/OpModal';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { searchVisitors, searchVisitorsToday } from 'store/slices/visitorSearchSlice';
import { useTranslation } from 'react-i18next';

interface ModalFormProps {
    open: boolean;
    onOk: (data: any) => Promise<void>;
    onClose: () => void;
}

const VisitorListModal: React.FC<ModalFormProps> = ({ open, onOk, onClose }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const lastSearchParams = useSelector((state: RootState) => state.visitorSearch.lastSearchParams);
    const globalLocationId = useSelector((state: RootState) => state.locations.globalLocation?.id);

    const [form] = OpForm.useForm();
    const { invitationConfig } = useSelector((state: RootState) => state.visitorInvitation);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { t } = useTranslation();

    return (
        <OpModal
            open={open}
            title={t('visitors.visitorList.add')}
            okText={t('visitors.visitorList.save')}
            cancelText={t('visitors.visitorList.cancel')}
            onCancel={() => {
                form.resetFields();
                onClose();
            }}
            onOk={async () => {
                if (isSubmitting) return;

                try {
                    await form.validateFields();
                    setIsSubmitting(true);
                    await form.submit();
                } catch (errorInfo) {
                    // Validation failed - error messages will be shown under form fields
                    // Do not close modal
                    setIsSubmitting(false);
                }
            }}
            centered
            closable={!isSubmitting}
            okButtonProps={{ disabled: isSubmitting, loading: isSubmitting }}
            cancelButtonProps={{ disabled: isSubmitting }}
        >
            <OpForm
                form={form}
                disabled={isSubmitting}
                onSubmit={async ({ values }) => {
                    try {
                        const trimmedValues = Object.fromEntries(
                            Object.entries(values).map(([key, value]) => [
                                key,
                                typeof value === 'string' ? value.trim() : value,
                            ])
                        );
                        await onOk(trimmedValues);
                        await dispatch(searchVisitorsToday({ orgId }));
                        if (Object.keys(lastSearchParams).length !== 0) {
                            await dispatch(searchVisitors({
                                ...lastSearchParams,
                                orgId,
                                siteId: lastSearchParams.siteId || globalLocationId!
                            }));
                        }
                        form.resetFields();
                        onClose();
                    } catch (error) {
                        // Error handled in parent, just reset submitting state
                        console.error('Failed to add visitor:', error);
                    } finally {
                        setIsSubmitting(false);
                    }
                }}
                hasError={false}
                defaultButtons={false}
            >
                <OpForm.Input
                    label={t('visitors.visitorList.firstName')} name="firstName"
                    rules={[{ required: true, message: t('visitors.visitorList.firstNameRequired') }]}
                />
                <OpForm.Input
                    label={t('visitors.visitorList.lastName')} name="lastName"
                    rules={[{ required: true, message: t('visitors.visitorList.lastNameRequired') }]}
                />
                {(invitationConfig?.email.included === 1) ? (
                    <OpForm.Input
                        label={t('visitors.visitorList.email')} name="email"
                        validateTrigger={['onChange', 'onBlur']}
                        rules={[
                            { required: invitationConfig.email.required === 1, message: t('visitors.visitorList.emailRequired') },
                            {
                                pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                message: t('visitors.visitorList.emailInvalid')
                            }
                        ]} />
                ) : undefined}
                {(invitationConfig?.visitPhone.included === 1) ? (
                    <OpForm.Input
                        label={t('visitors.visitorList.mobilePhone')} name="mobilePhone"
                        rules={[{ required: invitationConfig.visitPhone.required === 1, message: t('visitors.visitorList.mobilePhoneRequired') }]}
                    />
                ) : undefined}
            </OpForm>
        </OpModal>
    );
};

export default VisitorListModal;
